/**
 * Company Details Form Component
 * First step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { CompanyDetails, Address } from '../../services/registration.service';

interface CompanyDetailsFormProps {
  data?: CompanyDetails;
  onSave: (data: CompanyDetails) => Promise<void>;
  onNext: () => void;
  saving: boolean;
}

const ENTITY_TYPES = [
  { value: 'PROPRIETORSHIP', label: 'Proprietorship' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'LLP', label: 'Limited Liability Partnership (LLP)' },
  { value: 'PRIVATE_LIMITED', label: 'Private Limited Company' },
  { value: 'PUBLIC_LIMITED', label: 'Public Limited Company' }
];

const FACILITY_TYPES = [
  { value: 'OWNED', label: 'Owned' },
  { value: 'LEASED', label: 'Leased' },
  { value: 'CONTRACT_MANUFACTURING', label: 'Contract Manufacturing' }
];

export function CompanyDetailsForm({ data, onSave, onNext, saving }: CompanyDetailsFormProps) {
  const [formData, setFormData] = useState<CompanyDetails>({
    registeredName: '',
    entityType: 'PRIVATE_LIMITED',
    dateOfIncorporation: new Date(),
    countryOfIncorporation: 'India',
    registeredAddress: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    manufacturingAddress: undefined,
    facilityType: 'OWNED'
  });

  const [sameAsRegistered, setSameAsRegistered] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      // Ensure dateOfIncorporation is a Date object
      const processedData = {
        ...data,
        dateOfIncorporation: data.dateOfIncorporation instanceof Date 
          ? data.dateOfIncorporation 
          : new Date(data.dateOfIncorporation)
      };
      setFormData(processedData);
      setSameAsRegistered(!data.manufacturingAddress);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.registeredName.trim()) {
      newErrors.registeredName = 'Registered company name is required';
    }

    if (!formData.entityType) {
      newErrors.entityType = 'Entity type is required';
    }

    if (!formData.dateOfIncorporation) {
      newErrors.dateOfIncorporation = 'Date of incorporation is required';
    }

    // Validate registered address
    if (!formData.registeredAddress.addressLine1.trim()) {
      newErrors['registeredAddress.addressLine1'] = 'Address line 1 is required';
    }
    if (!formData.registeredAddress.city.trim()) {
      newErrors['registeredAddress.city'] = 'City is required';
    }
    if (!formData.registeredAddress.state.trim()) {
      newErrors['registeredAddress.state'] = 'State is required';
    }
    if (!formData.registeredAddress.pincode.trim()) {
      newErrors['registeredAddress.pincode'] = 'PIN code is required';
    } else if (!/^\d{6}$/.test(formData.registeredAddress.pincode)) {
      newErrors['registeredAddress.pincode'] = 'PIN code must be 6 digits';
    }

    // Validate manufacturing address if different
    if (!sameAsRegistered && formData.manufacturingAddress) {
      if (!formData.manufacturingAddress.addressLine1.trim()) {
        newErrors['manufacturingAddress.addressLine1'] = 'Manufacturing address line 1 is required';
      }
      if (!formData.manufacturingAddress.city.trim()) {
        newErrors['manufacturingAddress.city'] = 'Manufacturing city is required';
      }
      if (!formData.manufacturingAddress.state.trim()) {
        newErrors['manufacturingAddress.state'] = 'Manufacturing state is required';
      }
      if (!formData.manufacturingAddress.pincode.trim()) {
        newErrors['manufacturingAddress.pincode'] = 'Manufacturing PIN code is required';
      } else if (!/^\d{6}$/.test(formData.manufacturingAddress.pincode)) {
        newErrors['manufacturingAddress.pincode'] = 'Manufacturing PIN code must be 6 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      manufacturingAddress: sameAsRegistered ? undefined : formData.manufacturingAddress
    };

    try {
      await onSave(submitData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  const handleAddressChange = (
    addressType: 'registeredAddress' | 'manufacturingAddress',
    field: keyof Address,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));
  };

  const handleSameAsRegisteredChange = (checked: boolean) => {
    setSameAsRegistered(checked);
    if (checked) {
      setFormData(prev => ({
        ...prev,
        manufacturingAddress: undefined
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        manufacturingAddress: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India'
        }
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Registered Company Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Registered Company Name *
          </label>
          <Input
            value={formData.registeredName}
            onChange={(e) => setFormData(prev => ({ ...prev, registeredName: e.target.value }))}
            placeholder="Enter registered company name as per MCA/GST records"
            error={errors.registeredName}
          />
        </div>

        {/* Entity Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Entity Type *
          </label>
          <select
            value={formData.entityType}
            onChange={(e) => setFormData(prev => ({ ...prev, entityType: e.target.value as any }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827' }}
          >
            {ENTITY_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors.entityType && (
            <p className="mt-1 text-sm text-red-600">{errors.entityType}</p>
          )}
        </div>

        {/* Date of Incorporation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date of Incorporation *
          </label>
          <Input
            type="date"
            value={formData.dateOfIncorporation instanceof Date 
              ? formData.dateOfIncorporation.toISOString().split('T')[0]
              : new Date(formData.dateOfIncorporation).toISOString().split('T')[0]
            }
            onChange={(e) => setFormData(prev => ({ ...prev, dateOfIncorporation: new Date(e.target.value) }))}
            error={errors.dateOfIncorporation}
          />
        </div>
      </div>

      {/* Registered Address */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Registered Office Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 1 *
            </label>
            <Input
              value={formData.registeredAddress.addressLine1}
              onChange={(e) => handleAddressChange('registeredAddress', 'addressLine1', e.target.value)}
              placeholder="Building number, street name"
              error={errors['registeredAddress.addressLine1']}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2
            </label>
            <Input
              value={formData.registeredAddress.addressLine2 || ''}
              onChange={(e) => handleAddressChange('registeredAddress', 'addressLine2', e.target.value)}
              placeholder="Area, locality (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City *
            </label>
            <Input
              value={formData.registeredAddress.city}
              onChange={(e) => handleAddressChange('registeredAddress', 'city', e.target.value)}
              placeholder="City"
              error={errors['registeredAddress.city']}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              State *
            </label>
            <Input
              value={formData.registeredAddress.state}
              onChange={(e) => handleAddressChange('registeredAddress', 'state', e.target.value)}
              placeholder="State"
              error={errors['registeredAddress.state']}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PIN Code *
            </label>
            <Input
              value={formData.registeredAddress.pincode}
              onChange={(e) => handleAddressChange('registeredAddress', 'pincode', e.target.value)}
              placeholder="6-digit PIN code"
              maxLength={6}
              error={errors['registeredAddress.pincode']}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <Input
              value={formData.registeredAddress.country}
              onChange={(e) => handleAddressChange('registeredAddress', 'country', e.target.value)}
              disabled
            />
          </div>
        </div>
      </div>

      {/* Manufacturing Address */}
      <div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="sameAsRegistered"
            checked={sameAsRegistered}
            onChange={(e) => handleSameAsRegisteredChange(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="sameAsRegistered" className="ml-2 text-sm text-gray-700">
            Manufacturing address is same as registered office address
          </label>
        </div>

        {!sameAsRegistered && (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Manufacturing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 1 *
                </label>
                <Input
                  value={formData.manufacturingAddress?.addressLine1 || ''}
                  onChange={(e) => handleAddressChange('manufacturingAddress', 'addressLine1', e.target.value)}
                  placeholder="Building number, street name"
                  error={errors['manufacturingAddress.addressLine1']}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 2
                </label>
                <Input
                  value={formData.manufacturingAddress?.addressLine2 || ''}
                  onChange={(e) => handleAddressChange('manufacturingAddress', 'addressLine2', e.target.value)}
                  placeholder="Area, locality (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <Input
                  value={formData.manufacturingAddress?.city || ''}
                  onChange={(e) => handleAddressChange('manufacturingAddress', 'city', e.target.value)}
                  placeholder="City"
                  error={errors['manufacturingAddress.city']}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <Input
                  value={formData.manufacturingAddress?.state || ''}
                  onChange={(e) => handleAddressChange('manufacturingAddress', 'state', e.target.value)}
                  placeholder="State"
                  error={errors['manufacturingAddress.state']}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PIN Code *
                </label>
                <Input
                  value={formData.manufacturingAddress?.pincode || ''}
                  onChange={(e) => handleAddressChange('manufacturingAddress', 'pincode', e.target.value)}
                  placeholder="6-digit PIN code"
                  maxLength={6}
                  error={errors['manufacturingAddress.pincode']}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility Type
                </label>
                <select
                  value={formData.facilityType || 'OWNED'}
                  onChange={(e) => setFormData(prev => ({ ...prev, facilityType: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                  style={{ color: '#111827' }}
                >
                  {FACILITY_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}